// src/components/notifications/NotificationBell.tsx
//
// Top-right notification dropdown. Hidden for anonymous users.
// Shows a count badge on the bell; clicking opens a panel with
// the latest items, "Mark all read" footer, and an empty state.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, CheckCheck, Inbox } from "lucide-react";
import { useNotifications } from "@/lib/notifications";
import { cn } from "@/lib/cn";
import { useTranslation } from "react-i18next";

interface Props {
  isAuthenticated: boolean;
}

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = (then - now) / 1000;
  const abs = Math.abs(diff);
  if (abs < 60) return RELATIVE.format(Math.round(diff), "second");
  if (abs < 3600) return RELATIVE.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return RELATIVE.format(Math.round(diff / 3600), "hour");
  return RELATIVE.format(Math.round(diff / 86400), "day");
}

export function NotificationBell({ isAuthenticated }: Props) {
const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const { items, unread, pulseAt, refresh, markRead, markAllRead, loading }
    = useNotifications(isAuthenticated, 30_000);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!isAuthenticated) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label={unread > 0 ? `${unread} unread notifications`
                              : "Notifications"}
        title={unread > 0 ? `${unread} unread notification${unread === 1 ? "" : "s"}`
                           : "No new notifications"}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "relative inline-flex h-9 w-9 items-center justify-center rounded-md",
          "text-fg-secondary transition-colors hover:bg-surface-elevated hover:text-fg-primary",
          // Glow state: persistent soft ring while there are unread,
          // plus a one-shot pulse on the moment the count went up.
          unread > 0 && "shadow-[0_0_0_4px_rgba(99,102,241,0.18)]",
        )}
      >
        <Bell className={cn(
          "h-4 w-4",
          unread > 0 && "text-accent-indigo",
        )} strokeWidth={1.5} />
        {unread > 0 && (
          <span
            key={pulseAt ?? "static"}
            aria-hidden
            className={cn(
              "absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full bg-accent-rose px-1",
              "text-[10px] font-510 leading-4 text-white",
              pulseAt && "animate-notif-pulse",
            )}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className={cn(
            "absolute right-0 top-11 z-50 w-[360px] max-w-[calc(100vw-2rem)]",
            "overflow-hidden rounded-lg border border-border bg-bg-elevated shadow-xl",
          )}
        >
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <h3 className="text-sm font-510 text-fg-primary">Notifications</h3>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="inline-flex items-center gap-1 text-xs text-fg-tertiary hover:text-fg-primary"
              >
                <CheckCheck className="h-3.5 w-3.5" /> {t("notifications.markAllRead")}
              </button>
            )}
          </div>

          <ul className="max-h-[420px] overflow-y-auto">
            {loading && items.length === 0 && (
              <li className="px-3 py-6 text-center text-xs text-fg-tertiary">
                Loading…
              </li>
            )}
            {!loading && items.length === 0 && (
              <li className="px-3 py-10 text-center text-fg-tertiary">
                <Inbox className="mx-auto mb-2 h-6 w-6" strokeWidth={1.2} />
                <p className="text-sm">{t("notifications.empty")}</p>
                <p className="mt-1 text-xs">
                  Order updates, import jobs, and system messages will land here.
                </p>
              </li>
            )}
            {items.map((n) => (
              <li
                key={n.id}
                className={cn(
                  "border-b border-border/60 px-3 py-2.5 last:border-b-0",
                  !n.read_at && "bg-accent-indigo/[0.04]",
                )}
              >
                <NotificationRow n={n} onMarkRead={() => void markRead([n.id])} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Refresh trigger when reopening */}
      {open && <RefreshOnce onRefresh={refresh} />}
    </div>
  );
}

function NotificationRow({
  n, onMarkRead,
}: { n: import("@/lib/notifications").Notification; onMarkRead: () => void }) {
  const { t } = useTranslation();
  const body = (
    <div className="flex items-start gap-2">
      <span
        className={cn(
          "mt-1 inline-block h-2 w-2 shrink-0 rounded-full",
          n.read_at ? "bg-fg-quaternary" : "bg-accent-indigo",
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="truncate text-sm font-510 text-fg-primary">
            {n.title}
          </p>
          <span className="shrink-0 text-[10px] text-fg-quaternary">
            {n.created_at ? timeAgo(n.created_at) : ""}
          </span>
        </div>
        {n.body && (
          <p className="mt-0.5 line-clamp-2 text-xs text-fg-tertiary">
            {n.body}
          </p>
        )}
        <p className="mt-0.5 text-[10px] uppercase tracking-wider text-fg-quaternary">
          {n.kind}
        </p>
      </div>
      {!n.read_at && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation();
                             onMarkRead(); }}
          className="text-fg-quaternary hover:text-fg-primary"
          title={t("notifications.markAsRead")}
        >
          <Check className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
  if (n.link) {
    return (
      <Link to={n.link} className="block hover:opacity-90">
        {body}
      </Link>
    );
  }
  return body;
}

function RefreshOnce({ onRefresh }: { onRefresh: () => Promise<void> }) {
  useEffect(() => { void onRefresh(); }, [onRefresh]);
  return null;
}
